import { memo, useCallback, useRef, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import styles from './ProjectListWidget.module.scss';
import { fetchJson } from '../../../../helpers/fetch.js';
import { useMonitoringColumnDefs } from '../../../monitoring/useMonitoringColumnDefs.jsx';
import { useProjectFilter } from '../../hooks/useProjectFilter.js';
import SkeletonGrid from '../../../../skeleton_loaders/common/SkeletonGrid.jsx';
import withSuspense from '../../../../helpers/withSuspense.jsx';
const ServerSidePaginationAggrid = withSuspense(lazy(() => import('../../../common/ServerSidePaginationAggrid.jsx')), <SkeletonGrid />);

import {
  monitoring_list_page_path,
  monitoring_info_path,
  monitoring_column_settings_path
} from '~/routes';

const PAGINATION_PAGE_SIZE = 10;
const PAGINATION_PAGE_SIZE_SELECTOR = [10, 25, 50];

function ProjectListWidget({ widgetId, widgetRef, theme }) {
  const preset_id = useSelector(s => s.dashboard.widgets[widgetId].settings.preset_id) || null;
  const paginationPageSize = useRef(PAGINATION_PAGE_SIZE);
  const { activeProjectIds, isFiltered } = useProjectFilter();
  const effectiveProjectIds = isFiltered ? activeProjectIds.map(p => p.id) : [];

  const {data: {sorted_columns = [], default_visible_columns = [], visible_columns = []} = {} } = useQuery({
    queryKey: ['monitoring-column-settings', preset_id],
    queryFn:  () => fetchJson(monitoring_column_settings_path({preset_id: preset_id})),
    enabled: true
  });

  const { columnDefs } = useMonitoringColumnDefs({ sorted_columns, default_visible_columns, visible_columns });

  const getContextMenuItems = useCallback((params) => {
    const defaultItems = ['copy', 'copyWithHeaders', 'paste', 'separator', 'export'];
    const additionalItems = [];

    const projectId = params.node?.data?.project_id?.id;
    if (projectId) {
      additionalItems.push('separator', {
        name: I18n.t('open_link_in_new_tab'),
        action: () => {
          window.open(monitoring_info_path({ project_id: projectId }), '_blank');
        },
        icon: '<i class="fa fa-link"></i>',
      });
    }

    return [...defaultItems, ...additionalItems];
  }, []);

  const fetchBody = {
    ...(preset_id && {columns_preset_id: preset_id}),
    ...(effectiveProjectIds.length ? { project_ids: effectiveProjectIds } : {})
  };

  return (
    <div className={styles['applicant-project-list-container']}>
      <ServerSidePaginationAggrid
        key={preset_id}
        fetchPath={monitoring_list_page_path()}
        columnDefs={columnDefs}
        defaultGridOptions={{
          popupParent: document.body,
          pagination: true,
          paginationPageSizeSelector: PAGINATION_PAGE_SIZE_SELECTOR,
          paginationPageSize: paginationPageSize.current,
          cacheBlockSize: paginationPageSize.current,
          domLayout: 'normal',
          getContextMenuItems,
        }}
        refreshBodyChanges={true}
        resizeToRowsHeight={false}
        style={{ height: '100%' }}
        fetchBody={fetchBody}
      />
    </div>
  );
}

export default ProjectListWidget;
